import { useEffect, useState, useContext, useCallback } from "react";
import { useParams } from "react-router";

import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { format, parseISO } from "date-fns";

import {
  loadPathByIdAsync,
  getPathType,
  copyLinkToClipboard,
  getPathsListRoute,
} from "@/api/paths";
import type { PathData } from "@/api/paths";
import { getLevelNameFromRankName } from "@/api/levels";
import { PathsContext } from "@/context/PathsContext";

import PathDetailsAdmin from "@/components/paths/PathDetailsAdmin";
import LessonsContainerAdmin from "@/components/paths/LessonsContainerAdmin";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Lesson {
  id: string | number;
  name: string;
  durationNumber?: number;
  required?: boolean;
  [key: string]: unknown;
}

interface PathDetailState {
  pathDetails: Partial<PathData> & {
    lessonsCount?: number;
    totalDuration?: number;
    difficulty?: string;
    versions?: { lessons?: Lesson[]; updatedAt?: string }[];
  };
  lessons: Lesson[];
}

interface VersionOption {
  label: string;
  value: number;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const PathDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { showToast } = useToast();
  const { allPathNames, loadPathsAsync } = useContext(PathsContext);

  const [pathData, setPathData] = useState<PathDetailState | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedVersionIndex, setSelectedVersionIndex] = useState(0);

  // Derive parent route + label for breadcrumb based on path type
  const parentRoute = getPathsListRoute(pathData?.pathDetails as PathData | null);
  const parentLabel =
    parentRoute === AdminAppRoutes.routes.paths.routes.recommended?.route
      ? "Recommended Paths"
      : parentRoute === AdminAppRoutes.routes.paths.routes.quests?.route
        ? "Quests"
        : "Custom Paths";

  // Parent breadcrumb (resets trail to produce a clean parent → child trail)
  useBreadcrumb(
    pathData?.pathDetails?.name != null
      ? { label: parentLabel, route: parentRoute, display: "page", resetTrail: true }
      : null
  );

  // Current path breadcrumb
  useBreadcrumb(
    pathData?.pathDetails?.name != null
      ? { label: pathData.pathDetails.name!, route: `${parentRoute}/${id}`, display: "page" }
      : null
  );

  const fetchPathData = useCallback(
    async (pathId: string) => {
      try {
        setLoading(true);
        const response = await loadPathByIdAsync(pathId);
        const pathDetails = response.role as PathData & {
          versions?: { lessons?: Lesson[]; updatedAt?: string }[];
          lessons?: Lesson[];
        };
        const lessons: Lesson[] = pathDetails.versions?.[0]?.lessons ?? pathDetails.lessons ?? [];
        const lessonsCount = pathDetails.lessonsCount || lessons.length;
        const totalDuration = lessons.reduce((sum, l) => sum + (l.durationNumber ?? 0), 0);
        const difficulty =
          getLevelNameFromRankName(pathDetails.rankName) ?? pathDetails.levelName ?? "Not set";

        setPathData({
          pathDetails: { ...pathDetails, lessonsCount, totalDuration, difficulty },
          lessons,
        });
      } catch (error) {
        console.error("Error fetching path data:", error);
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Failed to load path data",
          life: 3000,
        });
      } finally {
        setLoading(false);
      }
    },
    [showToast]
  );

  useEffect(() => {
    if (id) void fetchPathData(id);
    void loadPathsAsync();
  }, [id]);

  // Update lessons displayed when version changes
  useEffect(() => {
    if (!pathData?.pathDetails?.versions) return;
    const versionLessons: Lesson[] =
      pathData.pathDetails.versions?.[selectedVersionIndex]?.lessons ??
      (pathData.pathDetails.lessons as Lesson[]) ??
      [];
    const lessonsCount = pathData.pathDetails.lessonsCount || versionLessons.length;
    const totalDuration = versionLessons.reduce((sum, l) => sum + (l.durationNumber ?? 0), 0);

    setPathData(prev =>
      prev
        ? {
            ...prev,
            pathDetails: { ...prev.pathDetails, lessonsCount, totalDuration },
            lessons: versionLessons,
          }
        : null
    );
  }, [selectedVersionIndex]);

  const pathType = getPathType(pathData?.pathDetails);

  const versionOptions: VersionOption[] =
    pathData?.pathDetails?.versions?.map((version, index) => {
      if (index === 0) return { label: "Latest Version", value: index };
      if (version.updatedAt) {
        try {
          return { label: format(parseISO(version.updatedAt), "MM/dd/yyyy HH:mm"), value: index };
        } catch {
          return { label: `Version ${index + 1}`, value: index };
        }
      }
      return { label: `Version ${index + 1}`, value: index };
    }) ?? [];

  const updatePathActivation = (isActivated: boolean) => {
    setPathData(prev =>
      prev ? { ...prev, pathDetails: { ...prev.pathDetails, activated: isActivated } } : null
    );
  };

  const updatePathRestriction = (isRestricted: boolean) => {
    setPathData(prev =>
      prev ? { ...prev, pathDetails: { ...prev.pathDetails, restricted: isRestricted } } : null
    );
  };

  const isShareDisabled =
    loading || !pathData?.pathDetails?.activated || !!pathData?.pathDetails?.restricted;

  const shareTooltip = loading
    ? "Loading..."
    : isShareDisabled
      ? !pathData?.pathDetails?.activated
        ? "Cannot share deactivated path"
        : "Cannot share restricted path"
      : "Copy link to clipboard";

  return (
    <div data-testid="admin-path-detail-page" className="pb-8">
      <div className="flex items-center justify-end mb-4">
        <Tooltip target=".share-button-tooltip" content={shareTooltip} position="left" />
        <div className="share-button-tooltip">
          <Button
            icon="pi pi-share-alt"
            severity="secondary"
            text
            plain
            onClick={() => copyLinkToClipboard(pathData?.pathDetails?.id, showToast)}
            disabled={isShareDisabled}
            aria-label={shareTooltip}
          />
        </div>
      </div>

      <PathDetailsAdmin
        pathDetails={pathData?.pathDetails ?? null}
        pathType={pathType}
        onActivationUpdate={updatePathActivation}
        onRestrictionUpdate={updatePathRestriction}
        loading={loading}
        allPathNames={allPathNames}
        lessons={pathData?.lessons ?? []}
        versionOptions={versionOptions}
        selectedVersionIndex={selectedVersionIndex}
        onVersionChange={setSelectedVersionIndex}
      />

      <LessonsContainerAdmin
        title="Lessons in this Path"
        lessons={pathData?.lessons ?? []}
        loading={loading}
      />
    </div>
  );
};

export default PathDetail;
